import React, { createContext, useState, useContext } from "react";
import { db } from "../firebase-config";
import {
	collection,
	query,
	where,
	getDocs,
	addDoc,
	updateDoc,
} from "firebase/firestore";

const AuthContext = createContext();

// Simple hash function using SHA-256
async function hashPassword(password) {
	const msgBuffer = new TextEncoder().encode(password);
	const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer);
	const hashArray = Array.from(new Uint8Array(hashBuffer));
	return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

const isAdminEmail = (email) => {
	const adminEmails = process.env.REACT_APP_ADMIN_EMAIL.replace("[", "")
		.replace("]", "")
		.split(",")
		.map((email) => email.trim().toLowerCase());
	return adminEmails.includes(email.toLowerCase());
};

export function useAuth() {
	return useContext(AuthContext);
}

export function AuthProvider({ children }) {
	const [currentUser, setCurrentUser] = useState(() => {
		const savedUser = localStorage.getItem("user");
		return savedUser ? JSON.parse(savedUser) : null;
	});
	// eslint-disable-next-line
	const [loading, setLoading] = useState(false);

	async function register(email, password) {
		try {
			// Check if email is authorized
			const authorizedQuery = query(
				collection(db, "authorizedEmails"),
				where("email", "==", email.toLowerCase())
			);
			const authorizedDocs = await getDocs(authorizedQuery);

			if (authorizedDocs.empty) {
				throw new Error("Email not authorized");
			}

			// Check if email is already registered
			const userQuery = query(
				collection(db, "users"),
				where("email", "==", email.toLowerCase())
			);
			const userDocs = await getDocs(userQuery);

			if (!userDocs.empty) {
				throw new Error("Email already registered");
			}

			// Hash password
			const hashedPassword = await hashPassword(password);

			// Create user
			const userDoc = await addDoc(collection(db, "users"), {
				email: email.toLowerCase(),
				password: hashedPassword,
				createdAt: new Date(),
				isAdmin: isAdminEmail(email),
			});

			// Update authorized email
			await updateDoc(authorizedDocs.docs[0].ref, { registered: true });

			const userData = {
				id: userDoc.id,
				email: email.toLowerCase(),
				isAdmin: isAdminEmail(email),
			};

			setCurrentUser(userData);
			localStorage.setItem("user", JSON.stringify(userData));
			return userData;
		} catch (error) {
			throw error;
		}
	}

	async function login(email, password) {
		try {
			const userQuery = query(
				collection(db, "users"),
				where("email", "==", email.toLowerCase())
			);
			const userDocs = await getDocs(userQuery);

			if (userDocs.empty) {
				throw new Error("Invalid email or password");
			}

			const userDoc = userDocs.docs[0];
			const user = userDoc.data();

			const hashedPassword = await hashPassword(password);
			if (hashedPassword !== user.password) {
				throw new Error("Invalid email or password");
			}

			const userData = {
				id: userDoc.id,
				email: user.email,
				isAdmin: isAdminEmail(user.email),
			};

			setCurrentUser(userData);
			localStorage.setItem("user", JSON.stringify(userData));
			return userData;
		} catch (error) {
			throw error;
		}
	}

	function logout() {
		setCurrentUser(null);
		localStorage.removeItem("user");
	}

	const value = {
		currentUser,
		register,
		login,
		logout,
	};

	return (
		<AuthContext.Provider value={value}>
			{!loading && children}
		</AuthContext.Provider>
	);
}
